/**
 * Portal do Trabalhador — integração com a API.
 *
 * Não usa o `httpClient` do sistema de propósito: aquele carrega o token do funcionário da
 * clínica, renova sessão e redireciona para `/login` quando expira. Nada disso vale aqui —
 * quem está do outro lado não tem conta, e mandá-lo para a tela de login do sistema seria
 * oferecer uma porta que ele não pode abrir.
 *
 * A sessão vive só em memória e no `sessionStorage`: fechou a aba, acabou. Prontuário aberto
 * num celular emprestado não pode continuar aberto amanhã.
 */

import type {
  AfastamentoDoTrabalhador,
  ExameDoTrabalhador,
  IdentidadeDoTrabalhador,
  SessaoDoPortal,
  TreinamentoDoTrabalhador,
} from '../types/portal';

const BASE = `${import.meta.env.VITE_API_URL ?? '/api/v1'}/portal`;
const CHAVE_DA_SESSAO = 'portal-sessao';

export class SessaoExpirada extends Error {
  constructor() {
    super('Sua sessão expirou. Peça um novo link com seu CPF.');
    this.name = 'SessaoExpirada';
  }
}

function lerSessao(): string | null {
  try {
    return sessionStorage.getItem(CHAVE_DA_SESSAO);
  } catch {
    // Navegador com armazenamento bloqueado: a sessão simplesmente não persiste.
    return null;
  }
}

function guardarSessao(token: string): void {
  try {
    sessionStorage.setItem(CHAVE_DA_SESSAO, token);
  } catch {
    /* segue sem persistir */
  }
}

function esquecerSessao(): void {
  try {
    sessionStorage.removeItem(CHAVE_DA_SESSAO);
  } catch {
    /* nada a fazer */
  }
}

async function buscar<T>(caminho: string): Promise<T> {
  const sessao = lerSessao();
  if (!sessao) throw new SessaoExpirada();

  const resposta = await fetch(`${BASE}/${caminho}`, {
    headers: { 'x-portal-sessao': sessao },
  });

  if (resposta.status === 401) {
    esquecerSessao();
    throw new SessaoExpirada();
  }
  if (!resposta.ok) throw new Error('Não foi possível carregar seus dados agora.');

  const corpo = (await resposta.json()) as { data: T };
  return corpo.data;
}

export const portalService = {
  temSessao: (): boolean => lerSessao() !== null,

  /** Pede o link. A resposta é a mesma para CPF que existe e para o que não existe. */
  async solicitarAcesso(cpf: string): Promise<string> {
    const resposta = await fetch(`${BASE}/acesso`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cpf }),
    });
    const corpo = (await resposta.json()) as { message?: string };
    return corpo.message ?? 'Se este CPF estiver cadastrado, enviamos um link de acesso.';
  },

  /** Troca o token do link por uma sessão. */
  async entrar(token: string): Promise<SessaoDoPortal> {
    const resposta = await fetch(`${BASE}/entrar`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (!resposta.ok) throw new SessaoExpirada();

    const corpo = (await resposta.json()) as { data: SessaoDoPortal };
    guardarSessao(corpo.data.tokenDeSessao);
    return corpo.data;
  },

  async sair(): Promise<void> {
    const sessao = lerSessao();
    esquecerSessao();
    if (!sessao) return;
    try {
      await fetch(`${BASE}/sair`, { method: 'POST', headers: { 'x-portal-sessao': sessao } });
    } catch {
      // A sessão local já foi embora; o servidor a expira sozinho.
    }
  },

  eu: () => buscar<IdentidadeDoTrabalhador>('eu'),
  exames: () => buscar<ExameDoTrabalhador[]>('meus-exames'),
  agendamentos: () => buscar<ExameDoTrabalhador[]>('meus-agendamentos'),
  treinamentos: () => buscar<TreinamentoDoTrabalhador[]>('meus-treinamentos'),
  afastamentos: () => buscar<AfastamentoDoTrabalhador[]>('meus-afastamentos'),
};

export default portalService;
